"use client";

import { Check, Copy, Loader2, LockKeyhole, LockKeyholeOpen } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { SITE_URL } from "@/lib/site-url";
import { useShareSnippet } from "../hooks";
import type { SnippetEmbedSettings } from "../types";
import { EmbedSnippetPanel } from "./EmbedSnippetPanel";

function buildShareUrl(locale: string, shareSlug: string): string {
  const site = SITE_URL.replace(/\/$/, "");
  return `${site}/${locale}/playground/s/${encodeURIComponent(shareSlug)}`;
}

export type ShareSnippetDropdownBodyProps = {
  locale: string;
  snippetId: string;
  initialShareSlug?: string | null;
  initialIsPasswordProtected?: boolean;
  /** The owner's embed opt-in, so the panel opens on the current state (`PG-EMBED01`). */
  initialEmbed?: SnippetEmbedSettings;
};

/**
 * Share URL + copy UI — used inside ShareSnippetPopover and ToolbarOverflowMenu share submenu.
 */
export function ShareSnippetDropdownBody({
  locale,
  snippetId,
  initialShareSlug,
  initialIsPasswordProtected = false,
  initialEmbed,
}: ShareSnippetDropdownBodyProps) {
  const t = useTranslations("playground");
  const shareMutation = useShareSnippet();
  const [shareSlug, setShareSlug] = useState<string | null>(initialShareSlug ?? null);
  const [isPasswordProtected, setIsPasswordProtected] = useState(
    initialIsPasswordProtected,
  );
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (initialShareSlug) setShareSlug(initialShareSlug);
  }, [initialShareSlug]);

  useEffect(() => {
    setIsPasswordProtected(initialIsPasswordProtected);
  }, [initialIsPasswordProtected]);

  const ensureShareSlug = useCallback(async () => {
    if (!snippetId) return null;
    if (shareSlug) return shareSlug;
    try {
      const snippet = await shareMutation.mutateAsync({ id: snippetId });
      const next = snippet.shareSlug;
      if (!next) {
        toast.error(t("shareFailed"));
        return null;
      }
      setShareSlug(next);
      return next;
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : t("shareFailed");
      toast.error(msg);
      return null;
    }
  }, [shareMutation, shareSlug, snippetId, t]);

  const fullUrl =
    shareSlug != null && shareSlug.length > 0 ? buildShareUrl(locale, shareSlug) : "";

  async function handleCopy() {
    const slug = shareSlug ?? (await ensureShareSlug());
    if (!slug) return;
    const url = buildShareUrl(locale, slug);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t("toastCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("toastCopyFailed"));
    }
  }

  async function handleProtectionChange(nextPassword: string | null) {
    if (nextPassword !== null && nextPassword.trim().length < 8) {
      toast.error(t("sharePasswordTooShort"));
      return;
    }
    try {
      const snippet = await shareMutation.mutateAsync({
        id: snippetId,
        password: nextPassword,
      });
      setShareSlug(snippet.shareSlug);
      setIsPasswordProtected(snippet.isPasswordProtected);
      setPassword("");
      toast.success(
        nextPassword === null ? t("shareProtectionRemoved") : t("shareProtectionSaved"),
      );
    } catch (requestError) {
      const message =
        requestError instanceof ApiError ? requestError.message : t("shareFailed");
      toast.error(message);
    }
  }

  return (
    <>
      <p className="mb-2 text-xs font-medium text-white/55">{t("shareLinkLabel")}</p>
      {shareMutation.isPending && !shareSlug ? (
        <div className="flex items-center gap-2 py-4 text-sm text-white/60">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          {t("shareGenerating")}
        </div>
      ) : (
        <>
          <div className="break-all rounded-lg border border-white/10 bg-[#0B0B0C] px-2 py-2 font-mono text-[11px] text-white/85">
            {fullUrl || "-"}
          </div>
          <button
            type="button"
            onClick={() => void handleCopy()}
            disabled={!fullUrl && shareMutation.isPending}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm font-medium text-white transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/40 disabled:opacity-50"
          >
            {copied ? (
              <Check className="h-4 w-4 text-emerald-400" aria-hidden />
            ) : (
              <Copy className="h-4 w-4 text-[#E94D1D]" aria-hidden />
            )}
            {fullUrl ? t("copyShareUrl") : t("createPublicShareUrl")}
          </button>
        </>
      )}
      <div className="mt-4 border-t border-white/10 pt-4">
        <div className="flex items-center gap-2">
          {isPasswordProtected ? (
            <LockKeyhole className="h-4 w-4 text-[#E94D1D]" aria-hidden />
          ) : (
            <LockKeyholeOpen className="h-4 w-4 text-white/45" aria-hidden />
          )}
          <p className="text-sm font-medium text-white">{t("shareProtectionTitle")}</p>
          <span className="ml-auto rounded-full border border-white/10 px-2 py-0.5 text-[11px] text-white/55">
            {isPasswordProtected ? t("shareProtected") : t("sharePublic")}
          </span>
        </div>
        <p className="mt-1 text-xs leading-5 text-white/50">{t("shareProtectionHelp")}</p>
        <label className="mt-3 block text-xs font-medium text-white/70" htmlFor="share-password">
          {t("sharePasswordLabel")}
        </label>
        <input
          id="share-password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder={t("sharePasswordPlaceholder")}
          className="mt-1.5 h-11 w-full rounded-lg border border-white/15 bg-[#0B0B0C] px-3 text-sm text-white outline-none transition placeholder:text-white/30 focus:border-[#E94D1D]/70 focus:ring-2 focus:ring-[#E94D1D]/20"
        />
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            disabled={shareMutation.isPending || password.length === 0}
            onClick={() => void handleProtectionChange(password)}
            className="inline-flex h-11 flex-1 items-center justify-center rounded-lg bg-[#E94D1D] px-3 text-sm font-semibold text-white transition hover:bg-[#d94318] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPasswordProtected ? t("shareUpdatePassword") : t("shareProtect")}
          </button>
          {isPasswordProtected ? (
            <button
              type="button"
              disabled={shareMutation.isPending}
              onClick={() => void handleProtectionChange(null)}
              className="inline-flex h-11 items-center justify-center rounded-lg border border-white/15 px-3 text-sm font-medium text-white/80 transition hover:bg-white/10 disabled:opacity-50"
            >
              {t("shareRemoveProtection")}
            </button>
          ) : null}
        </div>
      </div>
      <EmbedSnippetPanel
        locale={locale}
        snippetId={snippetId}
        shareSlug={shareSlug}
        isPasswordProtected={isPasswordProtected}
        {...(initialEmbed ? { initialEmbed } : {})}
      />
    </>
  );
}
